import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { trackAnalytics } from "@/services/analyticsTracking";
import {
  captureUTMs,
  promoteVisitorIdToLocalStorage,
  trackVisit,
} from "@/lib/visitorTracker";
import { captureUtmParams } from "@/lib/utmCapture";
import { onCookieConsentChange } from "@/lib/cookieConsent";

/**
 * Tracks page views on route changes.
 *
 * Cookie consent (LGPD):
 * - GA4 removido em 12/05/2026 (não inicializa mais Google).
 * - visitorTracker (UTM + public_visits): roda sempre, mas o consent controla
 *   a PERSISTÊNCIA — sem consent o visitor_id/UTM vivem só em sessionStorage
 *   (escopo da visita, dado de 1ª parte no nosso próprio banco). Quando aceita,
 *   são promovidos para localStorage (retenção cross-session).
 * - analytics_events (interno, sem 3rd-party): roda sempre — não viola LGPD.
 *
 * Momento da gravação (revisto em 17/09/2026): grava LOGO DEPOIS do primeiro
 * quadro, não na ociosidade. A espera por ociosidade (até 3 s) chegou aqui como
 * ganho de velocidade, mas criou um problema pior: visita curta — justamente o
 * ricochete que estamos medindo em campanha — saía sem registro, e a home ficava
 * comparável apenas a si mesma. A /entrada nunca teve essa espera, e é com ela
 * que a comparação é feita.
 *
 * O peso continua fora do caminho da primeira pintura por outro meio: o cliente
 * do Supabase entra por `import()` dentro de `trackVisit`/`trackAnalytics`, então
 * nada disso está no pacote inicial da página.
 */

/** Roda logo após o primeiro quadro pintado — sem esperar ociosidade. */
function depoisDoPrimeiroQuadro(fn: () => void): () => void {
  if (typeof window === "undefined") return () => undefined;
  const raf = window.requestAnimationFrame?.bind(window);
  if (!raf) {
    const id = window.setTimeout(fn, 0);
    return () => window.clearTimeout(id);
  }
  let cancelado = false;
  raf(() => {
    if (!cancelado) window.setTimeout(fn, 0);
  });
  return () => {
    cancelado = true;
  };
}
export function usePageView() {
  const location = useLocation();
  const hasTrackedOpen = useRef(false);

  // Captura de UTM/ângulo (sessão sempre; localStorage só com consent).
  useEffect(() => {
    captureUTMs();
    captureUtmParams(new URLSearchParams(location.search));
  }, [location.search]);

  // Ao aceitar depois, promove o mesmo visitor_id/UTM para localStorage e
  // grava também as chaves usadas pelo cadastro (`m360:*`) com a URL atual.
  useEffect(() => {
    const off = onCookieConsentChange((s) => {
      if (s === "accepted") {
        captureUTMs();
        captureUtmParams(new URLSearchParams(window.location.search));
        promoteVisitorIdToLocalStorage();
      }
    });
    return off;
  }, []);

  useEffect(() => {
    const caminho = location.pathname;
    return depoisDoPrimeiroQuadro(() => trackVisit(caminho));
  }, [location.pathname]);


  // app_open interno (analytics_events): roda sempre — sem 3rd-party.
  useEffect(() => {
    if (hasTrackedOpen.current) return;
    hasTrackedOpen.current = true;
    const screen = location.pathname;
    return depoisDoPrimeiroQuadro(() => trackAnalytics({ event: "app_open", screen }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
}
